import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronDown, FileText, FolderOpen, Mail, Paperclip, Printer, Trash2, X } from 'lucide-react';
import { htmlDeTexto } from '@malgoos/shared';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { EditorRico, htmlDoCampo, TextoRico } from '../../componentes/editor-rico';
import { Estado } from '../../componentes/estados';
import { AreaSensivel, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Menu } from '../../componentes/menu';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, post } from '../../nucleo/api';
import { BotaoLixeira } from '../../componentes/lixeira';
import { dataHora, dinheiro, email as emailFmt, nomeDeArquivo, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { caminhoDaNegociacao } from './crm-caminhos';
import { EscolherArquivosCentral, textoDosArquivos, type ArquivoCentral } from './CentralArquivos';
import { CaixaConfig } from './config-crm-base';
import { useOutrosServicos, usePortfolios } from './PortfoliosCentral';
import { chaveDaOpcao, comOutrosServicos, FOLGA_PDF_PROPOSTA, LIMITE_ARQUIVO_ANEXO, LIMITE_SOMA_ANEXOS, linkarEnderecos, prePortfolio, selecionarPortfolios, type LinhaPortfolio } from './portfolio-envio';
import type { Oportunidade } from './tipos';
import { CampoEmails, externosDoEnvio, MAX_DESTINOS, tagsDeTratamento } from './email-destinos';
import { preencherTags } from './email-tags';
import { caminhoDoPdfDaProposta, EscolherArquivosDaNegociacao, nomeDoPdf, type ArquivoDaNegociacao } from './email-anexos';
import { FormModeloEmail } from './ConfiguracoesCrm2';
import { useVoltar } from '../../nucleo/voltar';

/**
 * CRM no novo desenho, lote 4 (complemento 3 do clone do RD): propostas da negociação. A página "Nova proposta" com o modelo e os
 * textos ajustáveis, e "Criar proposta", "Criar e imprimir" e "Criar e enviar por e-mail"; a aba Propostas com a lista, o status
 * e a visualização com "Enviar por e-mail" e "Imprimir". A proposta guarda os itens da negociação do momento da criação.
 */

export interface ItemProposta {
  descricao: string;
  servico_nome: string | null;
  quantidade: number;
  preco_centavos: number;
  desconto_centavos: number;
  desconto_tipo: 'percentual' | 'valor';
  desconto_percentual: number | null;
  recorrencia: string;
  total_centavos: number;
}
export interface Proposta {
  id: string;
  oportunidade_id: string;
  numero: number;
  modelo_nome: string;
  cor: string;
  titulo: string;
  apresentacao: string;
  condicoes: string;
  rodape: string;
  /** Lote crm4 (L9): HTML do editor (limpo no servidor); null = proposta de antes do editor (vale o texto). */
  apresentacao_html: string | null;
  condicoes_html: string | null;
  rodape_html: string | null;
  itens: ItemProposta[];
  total_centavos: number;
  moeda: string;
  estado: 'criada' | 'enviada' | 'aceita' | 'recusada';
  /** Lote crm6 (item 18): o status configurado (Configurações do CRM > Status de proposta); as regras leem `estado`. */
  status_id?: string | null;
  status_nome?: string | null;
  status_cor?: CorSelo | null;
  enviada_em: string | null;
  enviada_para: string | null;
  created_at: string;
  autor_nome: string | null;
}
/** Lote crm6 (item 18): status de proposta do workspace (os quatro de fábrica nascem na primeira leitura). */
export interface StatusProposta {
  id: string;
  nome: string;
  cor: CorSelo;
  estado_base: Proposta['estado'];
  ordem: number;
  ativo: boolean;
  fabrica: boolean;
  em_uso: number;
}
export const useStatusProposta = () => useQuery({ queryKey: ['pipeline', 'proposta-status'], queryFn: () => get<{ itens: StatusProposta[] }>('/pipeline/proposta-status'), staleTime: 60_000 });
/** Nomes que o servidor grava nos de fábrica (em pt): com o nome intacto, o rótulo sai no idioma do app. */
const NOME_FABRICA: Record<Proposta['estado'], string> = { criada: 'Criada', enviada: 'Enviada', aceita: 'Aceita', recusada: 'Recusada' };
export function rotuloStatusProposta(t: (k: string) => string, nome: string | null | undefined, estado: Proposta['estado']): string {
  if (!nome || nome === NOME_FABRICA[estado]) return t(`crm.proposta.estados.${estado}`);
  return nome;
}
export interface ModeloProposta {
  id: string;
  nome: string;
  cor: string;
  titulo: string;
  apresentacao: string;
  condicoes: string;
  rodape: string;
  apresentacao_html: string | null;
  condicoes_html: string | null;
  rodape_html: string | null;
  padrao: boolean;
  publicado: boolean;
}
const COR_ESTADO: Record<Proposta['estado'], CorSelo> = { criada: 'neutro', enviada: 'azul', aceita: 'verde', recusada: 'laranja' };
export const useModelosProposta = () => useQuery({ queryKey: ['pipeline', 'proposta-modelos'], queryFn: () => get<{ itens: ModeloProposta[] }>('/pipeline/proposta-modelos') });
const usePropostas = (id: string) => useQuery({ queryKey: ['pipeline', 'propostas', id], queryFn: () => get<{ itens: Proposta[] }>(`/pipeline/oportunidades/${id}/propostas`) });

/** O documento da proposta (na tela, na visualização e na impressão). */
type CamposDocumento = 'cor' | 'titulo' | 'apresentacao' | 'condicoes' | 'rodape' | 'itens' | 'total_centavos' | 'moeda';
export function DocumentoProposta({ p, cliente, contato }: { p: Pick<Proposta, CamposDocumento> & Partial<Pick<Proposta, 'apresentacao_html' | 'condicoes_html' | 'rodape_html'>>; cliente: string | null; contato: string | null }) {
  const { t } = useTranslation('pipeline');
  // Lote crm4 (L9): os três blocos com a formatação do editor (a proposta de antes do editor sai do texto, em parágrafos).
  const bloco = (k: 'apresentacao' | 'condicoes' | 'rodape') => htmlDoCampo(p[`${k}_html`], p[k]);
  // Rlote-9: os três blocos são texto livre (como o título): na tela saem pelo modo discreto, com os nomes do cliente e do contato.
  const livre = (x: string) => textoLivre(x, cliente, contato);
  const unico = p.itens.filter((i) => i.recorrencia === 'avulso').reduce((s, i) => s + i.total_centavos, 0);
  const recorrente = p.total_centavos - unico;
  return (
    <article className="flex min-w-0 flex-col gap-4 rounded-lg border border-borda bg-sup-1 p-4 text-sm text-tinta-1 sm:p-6" style={{ borderTop: `6px solid ${p.cor}` }} data-documento-proposta="">
      <header>
        <h2 className="text-xl font-bold" style={{ color: p.cor }}>
          {p.titulo ? textoLivre(p.titulo, cliente) : t('crm.proposta.semTitulo')}
        </h2>
        {/* Rodada rlote-8: o "A/C: contato | cliente" pelo modo discreto (a tela; o PDF enviado é gerado no servidor). */}
        {(contato || cliente) && <p className="mt-1">{t('crm.proposta.aosCuidados', { contato: nomePessoa(contato), cliente: nomePessoa(cliente) })}</p>}
        {p.apresentacao && <TextoRico className="mt-2" html={bloco('apresentacao')} texto={livre} data-bloco-proposta="apresentacao" />}
      </header>
      {/* rcrm4 VIS-B6: a 390 as cinco colunas rolam dentro do contêiner (região rotulada e focável, como a Tabela), não a página. */}
      <div className="min-w-0 overflow-x-auto" role="region" aria-label={t('crm.proposta.itensTabela')} tabIndex={0} data-itens-proposta-rolagem="">
        <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left" style={{ borderBottom: `2px solid ${p.cor}` }}>
            {(['produto', 'quantidade', 'desconto', 'preco', 'subtotal'] as const).map((c) => (
              <th key={c} scope="col" className="py-2 pr-3 font-semibold">
                {t(`crm.proposta.colunas.${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {p.itens.map((i, n) => (
            <tr key={n} className="border-b border-borda align-top">
              <td className="py-2 pr-3">
                <span className="font-semibold">{textoLivre(i.descricao)}</span>
                {i.servico_nome && i.servico_nome !== i.descricao && <span className="block text-xs">{i.servico_nome}</span>}
              </td>
              <td className="py-2 pr-3">{numero(i.quantidade, Math.min(4, (String(i.quantidade).split('.')[1] ?? '').length))}</td>
              <td className="py-2 pr-3">{i.desconto_centavos ? (i.desconto_tipo === 'percentual' ? `${numero(i.desconto_percentual ?? 0, 2)}%` : dinheiro(i.desconto_centavos, p.moeda)) : '0'}</td>
              <td className="py-2 pr-3">{dinheiro(i.preco_centavos, p.moeda)}</td>
              <td className="py-2 pr-3">
                <span className="font-semibold">{dinheiro(i.total_centavos, p.moeda)}</span>
                <span className="block text-xs">{t(`itens.recorrencia.${i.recorrencia}`)}</span>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
      <dl className="ml-auto flex w-full max-w-xs flex-col gap-1">
        {unico > 0 && recorrente > 0 && (
          <>
            <div className="flex justify-between">
              <dt>{t('crm.ficha.produtos.unico')}</dt>
              <dd>{dinheiro(unico, p.moeda)}</dd>
            </div>
            <div className="flex justify-between">
              <dt>{t('crm.ficha.produtos.recorrente')}</dt>
              <dd>{dinheiro(recorrente, p.moeda)}</dd>
            </div>
          </>
        )}
        <div className="flex justify-between border-t border-borda pt-1 font-bold">
          <dt>{t('crm.proposta.totalPedido')}</dt>
          <dd>{dinheiro(p.total_centavos, p.moeda)}</dd>
        </div>
      </dl>
      {p.condicoes && (
        <section>
          <h3 className="font-semibold">{t('crm.proposta.condicoes')}</h3>
          <TextoRico html={bloco('condicoes')} texto={livre} data-bloco-proposta="condicoes" />
        </section>
      )}
      {p.rodape && (
        <footer>
          <TextoRico html={bloco('rodape')} texto={livre} data-bloco-proposta="rodape" />
        </footer>
      )}
    </article>
  );
}

/**
 * Lista de 30/10 (B), item 18: imprimir a proposta é abrir o ARQUIVO dela (o PDF que vai no e-mail, igual à prévia) numa aba
 * própria, onde o navegador imprime ou salva. Antes era a impressão da página, que dependia do tema, do modo discreto e do
 * navegador e saía diferente do PDF enviado.
 */
export function abrirPdfDaProposta(id: string) {
  window.open(caminhoDoPdfDaProposta(id), '_blank', 'noopener,noreferrer');
}

/** Lote crm4 (L8): os tetos dos anexos (o servidor confere de novo, com o tipo pelos bytes). */
export const MAX_ANEXOS = 5;
/** Lote crm8 (item 4): o nome do PDF da proposta, igual ao do servidor (`nomeDoPdfDaProposta` do Pipeline) e ao da origem. */
export const nomeDoPdfDaProposta = nomeDoPdf;
/** Teto da SOMA dos anexos (28 MiB; lista de 03/11): o Resend aceita 40 MB em base64, cerca de 30 MB de arquivo. Ver `portfolio-envio.ts`. */
export const LIMITE_ANEXOS = LIMITE_SOMA_ANEXOS;
const EXTENSOES_ANEXO = /\.(jpe?g|png|gif|webp|pdf|docx?|xlsx?|pptx?|odt|ods|txt|csv)$/i;
const ACEITOS_ANEXO = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt,.csv';
/** Tamanho do arquivo em KB ou MB (uma casa). */
export const tamanhoArquivo = (b: number) => (b >= 1024 * 1024 ? `${numero(b / 1024 / 1024, 1)} MB` : `${numero(Math.max(1, Math.round(b / 1024)), 0)} KB`);
/** Texto do HTML do editor (vazio quando só há quebras e espaços). */
export const textoDoEditor = (html: string) => html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '').trim();
/** Os três blocos do editor para a prévia do documento (o texto só decide se o bloco aparece). */
export const blocosDaPrevia = (b: { apresentacao: string; condicoes: string; rodape: string }) => ({
  apresentacao: textoDoEditor(b.apresentacao),
  condicoes: textoDoEditor(b.condicoes),
  rodape: textoDoEditor(b.rodape),
  apresentacao_html: b.apresentacao,
  condicoes_html: b.condicoes,
  rodape_html: b.rodape,
});

/** "Criar e-mail" da negociação (também o da proposta): só para o e-mail do cliente ou de uma pessoa de contato. */
export function EmailNegociacao({
  op,
  inicial,
  proposta,
  semOutros = false,
  aoFechar,
  aoEnviar,
}: {
  op: Oportunidade;
  inicial: { assunto: string; texto: string };
  proposta?: Proposta;
  /** rl0311-4 SPEC-B1 e rl0311-5 ROB5-B1: o corpo abriu sem o "Conheça nossos outros serviços", que não carregou; a mesma leitura do corpo. */
  semOutros?: boolean;
  aoFechar: () => void;
  aoEnviar?: () => void;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const destinos = useQuery({ queryKey: ['pipeline', 'emails', op.id], queryFn: () => get<{ destinatarios: string[] }>(`/pipeline/oportunidades/${op.id}/emails`) });
  // Rodada rlote-8 (VIS-M1): os nomes que o modo discreto troca nos nomes de arquivo e no assunto são os do cliente E das pessoas
  // de contato, como na aba Arquivos da ficha (a mesma consulta da ficha que o "Escolher modelo" usa).
  const fichaContatos = useQuery({ queryKey: ['pipeline', 'crm', 'ficha', op.id], queryFn: () => get<{ contatos: { nome: string }[]; empresas_adicionais?: { nome: string }[] }>(`/pipeline/crm/negociacoes/${op.id}/ficha`) });
  const nomesArquivo = [op.parceiro_nome, ...(fichaContatos.data?.contatos ?? []).map((c) => c.nome), ...(fichaContatos.data?.empresas_adicionais ?? []).map((c) => c.nome)];
  // crm6 item 13: vários "Para" (a proposta segue com um só, o contato da negociação).
  const [para, setPara] = useState<string[]>([]);
  const [assunto, setAssunto] = useState(inicial.assunto);
  // Lote crm4 (L8): o corpo pelo editor com formatação (o servidor limpa de novo e tira o texto puro dele).
  // Lista de 03/11: na proposta, o endereço do "Conheça nossos outros serviços" vai como link (o texto puro não vira link em todo leitor).
  const [html, setHtml] = useState(() => linkarEnderecos(htmlDeTexto(inicial.texto)));
  const temTexto = !!textoDoEditor(html);
  // Lote crm4 (L8): anexos, enviados como arquivos da negociação na hora de mandar (o id volta para o envio).
  const [anexos, setAnexos] = useState<File[]>([]);
  // crm6 item 17: arquivos que já estão na negociação (vão pelo id, sem subir de novo).
  const [daNegociacao, setDaNegociacao] = useState<ArquivoDaNegociacao[]>([]);
  const [escolherArquivos, setEscolherArquivos] = useState(false);
  const pesoDaNegociacao = daNegociacao.reduce((x, a) => x + a.tamanho, 0);
  const enviados = useRef(new Map<File, string>());
  const entradaArquivo = useRef<HTMLInputElement>(null);
  const limitePorArquivo = `${numero(LIMITE_ARQUIVO_ANEXO / 1024 / 1024, 0)} MB`;
  // rl0311-2 ROB2-B5: com a proposta, a tela reserva a folga do PDF, que só nasce no servidor, para não recusar só no envio.
  const tetoDaTela = proposta ? LIMITE_ANEXOS - FOLGA_PDF_PROPOSTA : LIMITE_ANEXOS;
  const limiteDaTela = `${numero(tetoDaTela / 1024 / 1024, 0)} MB`;
  // Lote crm8 (item 4): com a proposta, o PDF dela vai em anexo e conta no teto (o servidor confere de novo).
  const maxAnexos = proposta ? MAX_ANEXOS - 1 : MAX_ANEXOS;
  // Lote crm8 (item 3): arquivos da Central de Arquivos, somados aos outros anexos no teto (quantidade e tamanho), e o texto
  // opcional que vai ao fim do corpo (o servidor o acrescenta e confere tudo de novo).
  const [central, setCentral] = useState<ArquivoCentral[]>([]);
  const [textoCentral, setTextoCentral] = useState('');
  const [escolherCentral, setEscolherCentral] = useState(false);
  const somaCentral = central.reduce((x, a) => x + a.tamanho, 0);
  const anexar = (lista: FileList | null) => {
    const novos = Array.from(lista ?? []);
    if (entradaArquivo.current) entradaArquivo.current.value = '';
    const todos = [...anexos, ...novos];
    if (todos.length + daNegociacao.length + central.length > maxAnexos) return toast.erro(t('crm.email.anexosDemais', { max: maxAnexos }));
    const ruim = novos.find((f) => !EXTENSOES_ANEXO.test(f.name));
    if (ruim) return toast.erro(t('crm.email.anexoTipo', { nome: nomeDeArquivo(ruim.name, ...nomesArquivo) }));
    // rl0311-1 SPEC-M3: o teto por arquivo aparece aqui, não só no clique em enviar.
    const grande = novos.find((f) => f.size > LIMITE_ARQUIVO_ANEXO);
    if (grande) return toast.erro(t('crm.email.anexoArquivoGrande', { nome: nomeDeArquivo(grande.name, ...nomesArquivo), limite: limitePorArquivo }));
    const total = todos.reduce((x, f) => x + f.size, 0) + pesoDaNegociacao + somaCentral;
    if (total > tetoDaTela) return toast.erro(t('crm.email.anexoGrande', { nome: nomeDeArquivo(novos.at(-1)?.name ?? '', ...nomesArquivo), limite: limiteDaTela }));
    setAnexos(todos);
  };
  const usarCentral = (escolhidos: ArquivoCentral[]) => {
    if (anexos.length + daNegociacao.length + escolhidos.length > maxAnexos) return toast.erro(t('crm.email.anexosDemais', { max: maxAnexos }));
    const total = anexos.reduce((x, f) => x + f.size, 0) + pesoDaNegociacao + escolhidos.reduce((x, a) => x + a.tamanho, 0);
    if (total > tetoDaTela) return toast.erro(t('crm.email.anexoGrande', { nome: nomeDeArquivo(escolhidos.at(-1)?.nome ?? '', ...nomesArquivo), limite: limiteDaTela }));
    // Lista de 30/10 (B), item 20: o texto modelo dos arquivos escolhidos vem para o texto do envio (que vai antes da assinatura).
    setTextoCentral((x) => textoDosArquivos(x, central, escolhidos));
    setCentral(escolhidos);
    setEscolherCentral(false);
  };
  const tirarDaCentral = (idArquivo: string) => {
    const resto = central.filter((y) => y.id !== idArquivo);
    setTextoCentral((x) => textoDosArquivos(x, central, resto));
    setCentral(resto);
  };
  // Lista de 03/11, "Regra final": ao abrir o e-mail da aba E-mails ou da proposta, o Geral e o portfólio de cada vertical que não
  // está na negociação entram na lista da Central, uma vez só (o que a pessoa tirar não volta quando a consulta atualiza nem ao reabrir
  // "Arquivos da Central"). As verticais da negociação (as do cadastro dela e as dos itens) são lidas a cada abertura. Consulta com erro:
  // aviso abaixo, e o envio segue.
  const outrosDoModelo = useOutrosServicos().data?.texto ?? '';
  // rl0311-8 VIS8-B2: o aviso de "não coube" segue o que está na lista agora.
  const quantidadeAtual = anexos.length + daNegociacao.length + central.length;
  const pesoAtual = anexos.reduce((x, f) => x + f.size, 0) + pesoDaNegociacao + somaCentral;
  const portfolios = usePortfolios();
  const marcacaoDaNegociacao = useQuery({
    queryKey: [`/verticais/pipeline.oportunidade/${op.id}`],
    queryFn: () => get<{ empresas: string[]; todas: boolean }>(`/verticais/pipeline.oportunidade/${op.id}`),
    staleTime: 0,
  });
  const [linhasPortfolio, setLinhasPortfolio] = useState<LinhaPortfolio[] | null>(null);
  const preMarcou = useRef(false);
  const portfoliosPronto = portfolios.isSuccess || portfolios.isError;
  // rl0311-8 ROB8-B1: só a leitura desta abertura vale: `isFetchedAfterMount` conta sucesso e erro, e o dado velho do cache não entra.
  const verticaisProntas = marcacaoDaNegociacao.isFetchedAfterMount;
  const [verticaisFalharam, setVerticaisFalharam] = useState(false);
  useEffect(() => {
    if (!portfoliosPronto || !verticaisProntas) return;
    const linhas = portfolios.data
      ? selecionarPortfolios(portfolios.data.itens, {
          verticaisDaNegociacao: marcacaoDaNegociacao.isSuccess ? { todas: marcacaoDaNegociacao.data.todas, ids: [...(marcacaoDaNegociacao.data.empresas ?? []), ...(op.itens ?? []).map((i) => i.vertical_id)] } : null,
          quantidadeAtual,
          bytesAtuais: pesoAtual,
          maxAnexos,
          ...(proposta ? {} : { folga: 0 }),
        })
      : [];
    const novos = prePortfolio(preMarcou.current, linhas);
    if (!novos) return;
    preMarcou.current = true;
    setVerticaisFalharam(!marcacaoDaNegociacao.isSuccess);
    setLinhasPortfolio(linhas);
    if (!novos.length) return;
    const juntos = [...central, ...novos.filter((n) => !central.some((c) => c.id === n.id))];
    setTextoCentral((x) => textoDosArquivos(x, central, juntos));
    setCentral(juntos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfoliosPronto, verticaisProntas]);
  const subirAnexos = async () => {
    const ids: string[] = [];
    for (const f of anexos) {
      let id = enviados.current.get(f);
      if (!id) {
        const fd = new FormData();
        fd.append('entidade', 'pipeline.oportunidade');
        fd.append('registro_id', op.id);
        fd.append('arquivo', f);
        id = (await api<{ id: string }>('POST', '/anexos', fd)).id;
        enviados.current.set(f, id);
      }
      ids.push(id);
    }
    return ids;
  };
  // Ajustes do CRM (27/10, F4): CC e CCO (só os endereços da negociação) e o modelo de e-mail com as tags do RD.
  const [cc, setCc] = useState<string[] | null>(null);
  const [cco, setCco] = useState<string[] | null>(null);
  const [escolherModelo, setEscolherModelo] = useState(false);
  const iniciou = useRef(false);
  useEffect(() => {
    if (iniciou.current || !destinos.data) return;
    iniciou.current = true;
    if (destinos.data.destinatarios[0]) setPara((x) => (x.length ? x : [destinos.data.destinatarios[0]!]));
  }, [destinos.data]);
  const internos = destinos.data?.destinatarios ?? [];
  // Rodada rlote-2 (SPEC-B3): a proposta segue a regra do e-mail solto (vários destinatários, endereço livre com o aviso).
  const externos = externosDoEnvio([para, cc ?? [], cco ?? []], internos);
  const totalDestinos = new Set([...para, ...(cc ?? []), ...(cco ?? [])]).size;
  const [confirmarExternos, setConfirmarExternos] = useState(false);
  const enviar = useMutation({
    mutationFn: async () => {
      // Lista de 30/10 (B), item 17: a proposta escolhida vai pelo id dela (o servidor gera o PDF); o resto, pelo id do arquivo.
      const ids = [...(await subirAnexos()), ...daNegociacao.filter((a) => !a.proposta).map((a) => a.id)];
      const propostas = daNegociacao.filter((a) => a.proposta).map((a) => a.id);
      const dasPropostas = propostas.length ? { propostas } : {};
      // Lote crm8 (item 3): os arquivos da Central e o texto opcional do fim do corpo.
      const daCentral = central.length ? { central: central.map((a) => a.id), ...(textoCentral.trim() ? { texto_central: textoCentral.trim() } : {}) } : {};
      // crm6 item 13 e rodada rlote-2 (SPEC-B3): o e-mail solto e a proposta aceitam vários destinatários e endereço livre
      // (com a confirmação); a proposta leva o PDF, CC, CCO e anexos (crm8 item 4).
      if (proposta) return post(`/pipeline/propostas/${proposta.id}/enviar`, { para, assunto, html, cc: cc ?? [], cco: cco ?? [], anexos: ids, ...dasPropostas, ...daCentral, ...(externos.length ? { confirmar_externos: true } : {}) });
      return post(`/pipeline/oportunidades/${op.id}/emails`, { para, assunto, html, cc: cc ?? [], cco: cco ?? [], anexos: ids, ...dasPropostas, ...daCentral, ...(externos.length ? { confirmar_externos: true } : {}) });
    },
    onSuccess: async () => {
      toast.sucesso(t('crm.email.enviado'));
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      await qc.invalidateQueries({ queryKey: ['chatter', 'pipeline.oportunidade', op.id] });
      aoEnviar?.();
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const s = useSessao();
  const semDestino = destinos.isSuccess && !destinos.data.destinatarios.length;
  // Rodada crm2 (SPEC-B2): sem leitura do Cadastros a lista vem vazia, mas os endereços existem.
  const semAcesso = semDestino && !s.pode('cadastros.ver');
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('crm.email.criar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={enviar.isPending} disabled={!para.length || totalDestinos > MAX_DESTINOS || !assunto.trim() || !temTexto || linhasPortfolio === null} onClick={() => (externos.length ? setConfirmarExternos(true) : enviar.mutate())} data-enviar-email="">
            {t('crm.email.enviar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-form-email="">
        {semDestino && <p className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2">{t(semAcesso ? 'crm.email.semAcessoCadastros' : 'crm.email.semDestino')}</p>}
        <Rotulo id={`${id}-de`} rotulo={t('crm.ficha2.email.de')} ajuda={t('crm.ficha2.email.deAjuda')}>
          <EntradaSensivel id={`${id}-de`} mascara={null} disabled value={nomePessoa(s.usuario.nome)} onChange={() => undefined} data-email-de="" />
        </Rotulo>
        <CampoEmails id={`${id}-para`} rotulo={t('crm.crm6.email.para')} dado="para" obrigatorio valor={para} aoMudar={setPara} sugestoes={internos} internos={internos} ajuda={t('crm.crm6.email.paraAjuda')} />
        {(cc === null || cco === null) && (
          <p className="-mt-2 flex gap-3 text-sm">
            {cc === null && (
              <button type="button" className="font-medium text-acento-forte hover:underline" onClick={() => setCc([])} data-email-abrir-cc="">
                {t('crm.ficha2.email.cc')}
              </button>
            )}
            {cco === null && (
              <button type="button" className="font-medium text-acento-forte hover:underline" onClick={() => setCco([])} data-email-abrir-cco="">
                {t('crm.ficha2.email.cco')}
              </button>
            )}
          </p>
        )}
        {cc !== null && <CampoEmails id={`${id}-cc`} rotulo={t('crm.ficha2.email.cc')} dado="cc" valor={cc} aoMudar={setCc} sugestoes={internos.filter((x) => !para.includes(x))} internos={internos} />}
        {cco !== null && <CampoEmails id={`${id}-cco`} rotulo={t('crm.ficha2.email.cco')} dado="cco" valor={cco} aoMudar={setCco} sugestoes={internos.filter((x) => !para.includes(x) && !(cc ?? []).includes(x))} internos={internos} />}
        {externos.length > 0 && (
          <p className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2" role="status" data-email-aviso-externos="">
            {t('crm.crm6.email.avisoExternos', { count: externos.length })}
          </p>
        )}
        {totalDestinos > MAX_DESTINOS && (
          <p className="text-xs text-perigo" role="alert" data-email-destinos-demais="">
            {t('crm.crm6.email.demais', { max: MAX_DESTINOS })}
          </p>
        )}
        <Rotulo id={`${id}-assunto`} rotulo={t('crm.email.assunto')} obrigatorio>
          <EntradaSensivel mascara={null} livre nomesNoTexto={nomesArquivo} id={`${id}-assunto`} data-email-assunto="" maxLength={200} value={assunto} onChange={(e) => setAssunto(e.target.value)} />
        </Rotulo>
        {!proposta && (
          <Botao variante="secundario" className="self-start" icone={<FileText className="size-4" />} onClick={() => setEscolherModelo(true)} data-email-modelo="">
            {t('crm.ficha2.email.selecionarModelo')}
          </Botao>
        )}
        <Rotulo id={`${id}-texto`} rotulo={t('crm.email.texto')} obrigatorio>
          <EditorRico id={`${id}-texto`} rotulo={t('crm.email.texto')} valor={html} aoMudar={setHtml} linhas={8} dado="email" />
        </Rotulo>
        <div className="flex flex-col gap-2" data-email-anexos="">
            <input ref={entradaArquivo} type="file" multiple hidden accept={ACEITOS_ANEXO} onChange={(e) => anexar(e.target.files)} data-email-arquivo="" />
            <div className="flex flex-wrap gap-2">
              <Botao variante="secundario" icone={<Paperclip className="size-4" />} onClick={() => entradaArquivo.current?.click()} data-email-anexar="">
                {t('crm.email.anexar')}
              </Botao>
              <Botao variante="secundario" icone={<FileText className="size-4" />} onClick={() => setEscolherArquivos(true)} data-email-anexar-da-negociacao="">
                {t('crm.crm6.arquivos.daNegociacao')}
              </Botao>
              <Botao variante="secundario" icone={<FolderOpen className="size-4" />} onClick={() => setEscolherCentral(true)} data-email-central="">
                {t('crm.cfg.central.escolher')}
              </Botao>
            </div>
            <p className="text-xs text-tinta-3">{t(proposta ? 'crm.email.anexosAjudaProposta' : 'crm.email.anexosAjuda', { max: maxAnexos, limite: limiteDaTela, porArquivo: limitePorArquivo })}</p>
            <div className="flex flex-col gap-1.5" data-email-portfolios="">
                <p className="text-sm font-medium text-tinta-2">{t('crm.cfg.central.envioPortfolioTitulo')}</p>
                <p className="text-xs text-tinta-3">{t('crm.cfg.central.envioPortfolioAjuda')}</p>
                {linhasPortfolio === null && (
                  <p role="status" className="text-xs text-tinta-3" data-email-portfolios-carregando="">
                    {t('crm.cfg.central.envioPortfolioCarregando')}
                  </p>
                )}
                {semOutros && (
                  <p role="alert" className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2" data-email-outros-erro="">
                    {t('crm.cfg.central.envioOutrosErro')}
                  </p>
                )}
                {verticaisFalharam && (
                  <p role="alert" className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2" data-email-verticais-erro="">
                    {t('crm.cfg.central.envioVerticaisErro')}
                  </p>
                )}
                {portfolios.isError && (
                  <p role="alert" className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2" data-email-portfolios-erro="">
                    {t('crm.cfg.central.envioPortfolioErro')}
                  </p>
                )}
                {linhasPortfolio && linhasPortfolio.length > 0 && (
                  <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda" aria-label={t('crm.cfg.central.envioPortfolioTitulo')}>
                    {linhasPortfolio.map((l) => {
                      const a = l.opcao.arquivo;
                      const opcao = l.opcao.tipo === 'geral' ? t('crm.cfg.central.portfolioGeral') : (l.opcao.nome ?? '');
                      const arquivo = a ? nomeDeArquivo(a.nome) : '';
                      const marcado = !!a && central.some((c) => c.id === a.id);
                      return (
                        <li key={l.chave} className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm" data-email-portfolio={chaveDaOpcao(l.opcao)} data-situacao={l.situacao}>
                          <CaixaConfig
                            marcado={marcado}
                            desabilitado={!a || enviar.isPending}
                            rotulo={a ? t('crm.cfg.central.envioPortfolioMarcar', { opcao, arquivo }) : t('crm.cfg.central.envioPortfolioSemArquivoNome', { opcao })}
                            aoMudar={(v) => {
                              if (!a) return;
                              if (!v) return tirarDaCentral(a.id);
                              usarCentral([...central, { autor_nome: null, ...a } as ArquivoCentral]);
                            }}
                          />
                          <span className="min-w-0 flex-1 truncate font-medium" title={a ? `${opcao}: ${arquivo}` : opcao}>
                            {opcao}
                            {a && <span className="font-normal text-tinta-3"> · {arquivo}</span>}
                          </span>
                          {a && <span className="numero hidden shrink-0 text-xs text-tinta-3 sm:inline">{tamanhoArquivo(a.tamanho)}</span>}
                          {l.situacao === 'sem_arquivo' && <p className="basis-full pl-9 text-xs text-tinta-3">{t('crm.cfg.central.envioPortfolioFalta')}</p>}
                          {l.situacao === 'nao_coube' && !marcado && a && (quantidadeAtual + 1 > maxAnexos || pesoAtual + a.tamanho > tetoDaTela) && (
                            <p role="status" className="basis-full pl-9 text-xs text-perigo">
                              {t('crm.cfg.central.envioPortfolioNaoCoube', { arquivo })}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
            </div>
            {(anexos.length + daNegociacao.length + central.length > 0 || proposta) && (
              <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda" aria-label={t('crm.email.anexos')}>
                {/* Lote crm8 (item 4): o PDF da proposta, gerado no envio (o nome é o da origem); não sai da lista. */}
                {proposta && (
                  <li className="flex items-center gap-2 px-3 py-2 text-sm" data-proposta-anexa="" data-email-anexo-proposta={nomeDoPdfDaProposta(proposta.numero, op.titulo)}>
                    <FileText className="size-4 shrink-0 text-perigo" aria-hidden />
                    {/* Rodada rlote-6 (VIS-M1): o título da negociação costuma levar o cliente (e e-mail): o nome visível sai do título já pelo
                        modo discreto (antes do slug, que esconderia o e-mail da máscara) e passa pelo `nomeDeArquivo`. */}
                    <span className="min-w-0 flex-1 truncate" title={nomeDeArquivo(nomeDoPdfDaProposta(proposta.numero, textoLivre(op.titulo, ...nomesArquivo)), ...nomesArquivo)}>
                      {nomeDeArquivo(nomeDoPdfDaProposta(proposta.numero, textoLivre(op.titulo, ...nomesArquivo)), ...nomesArquivo)}
                    </span>
                    <span className="hidden shrink-0 text-xs text-tinta-3 sm:inline">{t('crm.email.comProposta', { numero: proposta.numero, total: dinheiro(proposta.total_centavos, proposta.moeda) })}</span>
                  </li>
                )}
                {daNegociacao.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-email-anexo-negociacao={a.id}>
                    <Paperclip className="size-4 shrink-0 text-tinta-3" aria-hidden />
                    <span className="min-w-0 flex-1 truncate" title={nomeDeArquivo(a.nome, ...nomesArquivo)}>
                      {nomeDeArquivo(a.nome, ...nomesArquivo)}
                    </span>
                    <Selo>{t(a.proposta ? 'crm.crm6.arquivos.propostaSelo' : 'crm.crm6.arquivos.daNegociacaoSelo')}</Selo>
                    {!a.proposta && <span className="numero shrink-0 text-xs text-tinta-3">{tamanhoArquivo(a.tamanho)}</span>}
                    <Botao
                      tamanho="sm"
                      variante="fantasma"
                      disabled={enviar.isPending}
                      icone={<X className="size-4" />}
                      aria-label={t('crm.email.removerAnexo', { nome: nomeDeArquivo(a.nome, ...nomesArquivo) })}
                      title={t('crm.email.removerAnexo', { nome: nomeDeArquivo(a.nome, ...nomesArquivo) })}
                      onClick={() => setDaNegociacao((x) => x.filter((y) => y.id !== a.id))}
                    />
                  </li>
                ))}
                {anexos.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 text-sm" data-email-anexo={f.name}>
                    <Paperclip className="size-4 shrink-0 text-tinta-3" aria-hidden />
                    <span className="min-w-0 flex-1 truncate" title={nomeDeArquivo(f.name, ...nomesArquivo)}>
                      {nomeDeArquivo(f.name, ...nomesArquivo)}
                    </span>
                    <span className="numero shrink-0 text-xs text-tinta-3">{tamanhoArquivo(f.size)}</span>
                    <Botao
                      tamanho="sm"
                      variante="fantasma"
                      disabled={enviar.isPending}
                      icone={<X className="size-4" />}
                      aria-label={t('crm.email.removerAnexo', { nome: nomeDeArquivo(f.name, ...nomesArquivo) })}
                      title={t('crm.email.removerAnexo', { nome: nomeDeArquivo(f.name, ...nomesArquivo) })}
                      onClick={() => setAnexos((x) => x.filter((_, j) => j !== i))}
                      data-email-remover-anexo={f.name}
                    />
                  </li>
                ))}
                {central.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-email-anexo-central={a.nome}>
                    <FolderOpen className="size-4 shrink-0 text-tinta-3" aria-hidden />
                    <span className="min-w-0 flex-1 truncate" title={nomeDeArquivo(a.nome)}>
                      {nomeDeArquivo(a.nome)}
                    </span>
                    <Selo>{t('crm.cfg.central.daCentral')}</Selo>
                    <span className="numero hidden shrink-0 text-xs text-tinta-3 sm:inline">{tamanhoArquivo(a.tamanho)}</span>
                    <Botao
                      tamanho="sm"
                      variante="fantasma"
                      disabled={enviar.isPending}
                      icone={<X className="size-4" />}
                      aria-label={t('crm.cfg.central.removerNome', { nome: nomeDeArquivo(a.nome) })}
                      title={t('crm.cfg.central.removerNome', { nome: nomeDeArquivo(a.nome) })}
                      onClick={() => tirarDaCentral(a.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
            {central.length > 0 && (
              <Rotulo id={`${id}-texto-central`} rotulo={t('crm.cfg.central.texto')} ajuda={t('crm.cfg.central.textoAjuda', { n: textoCentral.length })}>
                <AreaSensivel id={`${id}-texto-central`} rows={3} maxLength={2000} placeholder={t('crm.cfg.central.textoPlaceholder')} value={textoCentral} onChange={(e) => setTextoCentral(e.target.value)} data-email-texto-central="" />
              </Rotulo>
            )}
        </div>
        {escolherArquivos && (
          <EscolherArquivosDaNegociacao
            op={op}
            nomes={nomesArquivo}
            semProposta={proposta?.id}
            ja={daNegociacao.map((a) => a.id)}
            aoFechar={() => setEscolherArquivos(false)}
            aoEscolher={(lista) => {
              const peso = lista.reduce((x, a) => x + a.tamanho, 0) + anexos.reduce((x, f) => x + f.size, 0) + somaCentral;
              if (lista.length + anexos.length + central.length > maxAnexos) return toast.erro(t('crm.email.anexosDemais', { max: maxAnexos }));
              if (peso > tetoDaTela) return toast.erro(t('crm.email.anexoGrande', { nome: nomeDeArquivo(lista.at(-1)?.nome ?? '', ...nomesArquivo), limite: limiteDaTela }));
              setDaNegociacao(lista);
              setEscolherArquivos(false);
            }}
          />
        )}
        {escolherCentral && <EscolherArquivosCentral inicial={central} aoFechar={() => setEscolherCentral(false)} aoEscolher={usarCentral} />}
        {escolherModelo && (
          <EscolherModeloEmail
            op={op}
            aoFechar={() => setEscolherModelo(false)}
            aoEscolher={(m) => {
              setAssunto(m.assunto);
              // Lista de 03/11, "Regra final": o modelo troca o corpo, mas o texto do site segue nele.
              // rl0311-8 SPEC8-B1: confere o endereço (ou o texto inteiro, sem endereço), não uma palavra solta.
              setHtml(outrosDoModelo && !m.html.includes(/https?:\/\/[^\s<>"']+/.exec(outrosDoModelo)?.[0] ?? outrosDoModelo) ? `${m.html}${linkarEnderecos(htmlDeTexto(outrosDoModelo))}` : m.html);
              setEscolherModelo(false);
            }}
          />
        )}
      </div>
      <Confirmar
        aberto={confirmarExternos}
        aoFechar={() => setConfirmarExternos(false)}
        carregando={enviar.isPending}
        titulo={t('crm.crm6.email.confirmarExternosTitulo')}
        texto={t('crm.crm6.email.confirmarExternosTexto', { lista: externos.map((x) => emailFmt(x)).join(', ') })}
        aoConfirmar={() => {
          setConfirmarExternos(false);
          enviar.mutate();
        }}
      />
    </Drawer>
  );
}


interface ModeloEmail {
  id: string;
  nome: string;
  assunto: string;
  corpo: string;
  corpo_html: string | null;
  compartilhado: boolean;
  meu: boolean;
  /** Lote crm6 (item 15): modelo pronto do CRM. */
  pronto?: boolean;
  created_at: string;
}

// crm6: as tags e o preenchimento moraram aqui; agora em `email-tags.ts` (as Configurações usam sem importar esta tela).
export { preencherTags, TAGS_EMAIL } from './email-tags';

/** Ajustes do CRM (27/10, F4): "Selecionar modelo de e-mail" (o modal do RD), com a prévia e "Configurar modelos". */
function EscolherModeloEmail({ op, aoFechar, aoEscolher }: { op: Oportunidade; aoFechar: () => void; aoEscolher: (m: { assunto: string; html: string }) => void }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const id = useId();
  const qc = useQueryClient();
  const modelos = useQuery({ queryKey: ['pipeline', 'email-modelos'], queryFn: () => get<{ itens: ModeloEmail[] }>('/pipeline/email-modelos') });
  // crm6 item 16: criar ou editar o modelo numa janela por cima, sem sair do e-mail (o rascunho fica como está).
  const [formModelo, setFormModelo] = useState<{ atual: ModeloEmail | null; base?: ModeloEmail } | null>(null);
  // Val5: criar e editar modelo de e-mail é configuração do CRM (`pipeline.configurar`, o mesmo das tools).
  const podeConfigurar = s.pode('pipeline.configurar');
  const ficha = useQuery({ queryKey: ['pipeline', 'crm', 'ficha', op.id], queryFn: () => get<{ contatos: { nome: string; cargo: string | null; principal: boolean; tratamento?: string | null }[] }>(`/pipeline/crm/negociacoes/${op.id}/ficha`) });
  const [escolhido, setEscolhido] = useState('');
  const m = modelos.data?.itens.find((x) => x.id === escolhido) ?? null;
  const contato = ficha.data?.contatos.find((c) => c.principal) ?? ficha.data?.contatos[0] ?? null;
  const valores = { 'contato-nome': contato?.nome ?? null, 'contato-cargo': contato?.cargo ?? null, 'deal-nome': op.titulo, 'deal-empresa': op.parceiro_nome, 'usuario-nome': s.usuario.nome, ...tagsDeTratamento(contato?.tratamento) };
  // Lote crm4 (L8): o corpo do modelo com a formatação (o de antes do editor vira parágrafos), com as tags escapadas.
  const corpoHtml = (x: ModeloEmail) => preencherTags(htmlDoCampo(x.corpo_html, x.corpo), valores, true);
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('crm.ficha2.email.modelosTitulo')}
      rodape={
        <>
          {podeConfigurar && (
            <Botao
              variante="secundario"
              onClick={() => setFormModelo(!m ? { atual: null } : m.pronto ? { atual: null, base: m } : { atual: m })}
              data-configurar-modelos={!m ? 'criar' : m.pronto ? 'base' : 'editar'}
            >
              {t(!m ? 'crm.crm6.modelos.criarAqui' : m.pronto ? 'crm.crm6.modelos.baseAqui' : 'crm.crm6.modelos.editarAqui')}
            </Botao>
          )}
          <Botao variante="primario" disabled={!m} onClick={() => m && aoEscolher({ assunto: preencherTags(m.assunto, valores), html: corpoHtml(m) })} data-usar-modelo="">
            {t('crm.ficha2.email.selecionar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Rotulo id={`${id}-modelo`} rotulo={t('crm.ficha2.email.modelos')}>
          <Selecao id={`${id}-modelo`} value={escolhido} vazio={t('crm.form.selecionar')} opcoes={(modelos.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.pronto ? t('crm.crm6.modelos.prontoNaLista', { nome: x.nome }) : textoLivre(x.nome) }))} onChange={(e) => setEscolhido(e.target.value)} data-escolher-modelo="" />
        </Rotulo>
        {modelos.isSuccess && !modelos.data.itens.length && <p className="text-sm text-tinta-3">{t('crm.ficha2.email.semModelos')}</p>}
        {m && (
          <div className="rounded-lg border border-borda bg-sup-1 p-3 text-sm" data-previa-modelo="">
            {/* Rlote-9: a prévia troca `{{usuario-nome}}` pelo nome de quem envia; ele também sai pelo modo discreto. */}
            <p className="font-semibold">{textoLivre(preencherTags(m.assunto, valores), op.parceiro_nome, contato?.nome, s.usuario.nome)}</p>
            <TextoRico className="mt-2 text-tinta-2" html={corpoHtml(m)} texto={(x) => textoLivre(x, op.parceiro_nome, contato?.nome, s.usuario.nome)} />
          </div>
        )}
      </div>
      {/* crm6 item 16: o formulário do modelo (o mesmo das Configurações do CRM, que no crm7 é em página) abre numa gaveta por
          cima do e-mail. */}
      {formModelo && (
        <Drawer aberto aoFechar={() => setFormModelo(null)} largura="lg" titulo={formModelo.atual ? t('crm.ficha2.config.modelosEmail.editarTitulo') : t('crm.ficha2.config.modelosEmail.criar')}>
          <FormModeloEmail
            atual={formModelo.atual}
            base={formModelo.base}
            aoFechar={() => setFormModelo(null)}
            aoSalvar={async (novo) => {
              await qc.invalidateQueries({ queryKey: ['pipeline', 'email-modelos'] });
              if (novo) setEscolhido(novo);
            }}
          />
        </Drawer>
      )}
    </Modal>
  );
}

/** O texto do e-mail da proposta; `outros` = "Conheça nossos outros serviços" (lista de 03/11), antes da assinatura; vazio não entra. */
const textoDoEmail = (t: (k: string, v?: Record<string, unknown>) => string, contato: string | null, autor: string, outros = '') =>
  comOutrosServicos(t('crm.email.textoProposta', { saudacao: contato ? t('crm.email.ola', { nome: contato.split(' ')[0] }) : t('crm.email.olaSemNome'), autor }), outros);

/** Aba Propostas da ficha. */
export function AbaPropostas({ op, contato, podeEditar }: { op: Oportunidade; contato: string | null; podeEditar: boolean }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const q = usePropostas(op.id);
  const [ver, setVer] = useState<Proposta | null>(null);
  const [email, setEmail] = useState<Proposta | null>(null);
  const outros = useOutrosServicos();
  const status = useStatusProposta();
  const estado = useMutation({
    mutationFn: ({ id, ...v }: { id: string; estado?: Proposta['estado']; status_id?: string }) => post(`/pipeline/propostas/${id}/estado`, v),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['pipeline', 'propostas', op.id] }), qc.invalidateQueries({ queryKey: ['pipeline', 'proposta-status'] })]),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Ajustes do CRM (27/10, F8): sem produto, o aviso do RD ("Adicione produtos à negociação") com o atalho para a aba Produtos.
  const [semProdutos, setSemProdutos] = useState(false);
  const criar = () => (op.itens?.length ? navegar(`${caminhoDaNegociacao(op.id)}/proposta`) : setSemProdutos(true));
  const PROXIMO: Partial<Record<Proposta['estado'], Proposta['estado']>> = { criada: 'enviada', enviada: 'aceita' };
  // Lista de 04/11 (item 1): excluir pede `pipeline.excluir`, a negociação aberta e a proposta não aceita, como a rota.
  const podeExcluir = s.pode('pipeline.excluir');
  const [excluindo, setExcluindo] = useState<Proposta | null>(null);
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pipeline/propostas/${id}`),
    onSuccess: async () => {
      toast.sucesso(t('crm.proposta.excluida'));
      setExcluindo(null);
      await Promise.all([qc.invalidateQueries({ queryKey: ['pipeline', 'propostas', op.id] }), qc.invalidateQueries({ queryKey: ['pipeline', 'proposta-status'] })]);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <section className="flex flex-col gap-3" data-aba-propostas="">
      {((podeEditar && op.estado === 'aberta') || podeExcluir) && (
        <div className="flex flex-wrap justify-end gap-2">
          {podeExcluir && <BotaoLixeira entidades={['pipeline.proposta']} />}
          {podeEditar && op.estado === 'aberta' && (
          <Botao icone={<FileText className="size-4" />} onClick={criar} data-criar-proposta="">
            {t('crm.proposta.criar')}
          </Botao>
          )}
        </div>
      )}
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-borda px-4 py-8 text-center text-sm text-tinta-3" data-propostas-vazio="">
              <p>{t('crm.ficha2.propostasVazio')}</p>
              {podeEditar && op.estado === 'aberta' && (
                <Botao icone={<FileText className="size-4" />} onClick={criar}>
                  {t('crm.proposta.criar')}
                </Botao>
              )}
            </div>
          ) : (
            <div className="scroll-pr-12 overflow-x-auto rounded-xl border border-borda bg-sup-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda bg-sup-2 text-left">
                    {(['proposta', 'modelo', 'criada', 'valor', 'status'] as const).map((c) => (
                      <th key={c} scope="col" className="h-11 px-4 py-2 text-xs font-semibold tracking-wider whitespace-nowrap text-tinta-3 uppercase">
                        {t(`crm.proposta.lista.${c}`)}
                      </th>
                    ))}
                    {podeExcluir && op.estado === 'aberta' && (
                      <th scope="col" className="sticky right-0 w-px bg-sup-2">
                        <span className="sr-only">{t('crm.proposta.acoes')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {d.itens.map((p) => (
                    <tr key={p.id} className="border-t border-borda" data-proposta={p.numero}>
                      <td className="px-4 py-3">
                        <button type="button" className="font-medium text-acento-forte hover:underline" onClick={() => setVer(p)} data-ver-proposta={p.numero}>
                          {t('crm.proposta.numero', { numero: p.numero })}
                        </button>
                      </td>
                      <td className="px-4 py-3">{p.modelo_nome}</td>
                      <td className="numero px-4 py-3">{dataHora(p.created_at)}</td>
                      <td className="numero px-4 py-3">{dinheiro(p.total_centavos, p.moeda)}</td>
                      <td className="px-4 py-3">
                        {/* R44: o status muda por um menu de ações (lista não grava no onChange). rl0411-3 (VIS-B2): o conteúdo quebra a linha dentro da célula, e a seta não fica sob a coluna fixa. */}
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span data-status-proposta={p.numero}>
                            <Selo cor={p.status_cor ?? COR_ESTADO[p.estado]}>{rotuloStatusProposta(t, p.status_nome, p.estado)}</Selo>
                          </span>
                          {podeEditar && PROXIMO[p.estado] && (
                            <Botao tamanho="sm" variante="fantasma" onClick={() => estado.mutate({ id: p.id, estado: PROXIMO[p.estado]! })} data-avancar-status={p.numero}>
                              {t('crm.ficha2.avancarStatus')}
                            </Botao>
                          )}
                          {/* Lote crm6 (item 18): os status ativos configurados, num menu solto (no body), que não é cortado pela tabela. */}
                          {podeEditar && (
                            <Menu
                              rotulo={t('crm.proposta.mudarStatus', { numero: p.numero })}
                              classeGatilho="rounded p-1 text-tinta-3 hover:bg-sup-3"
                              gatilho={<ChevronDown className="size-4" aria-hidden />}
                              solto={{ direita: true }}
                              data-mudar-status={String(p.numero)}
                              itens={(status.data?.itens ?? [])
                                .filter((x) => x.ativo && x.id !== p.status_id)
                                .map((x) => ({ chave: x.id, rotulo: rotuloStatusProposta(t, x.nome, x.estado_base), aoClicar: () => estado.mutate({ id: p.id, status_id: x.id }) }))}
                            />
                          )}
                        </span>
                      </td>
                      {podeExcluir && op.estado === 'aberta' && (
                        <td className="sticky right-0 w-px bg-sup-1 px-1 py-1">
                          {p.estado !== 'aceita' && (
                            <button
                              type="button"
                              aria-label={t('crm.proposta.excluir', { numero: p.numero })}
                              title={t('crm.proposta.excluir', { numero: p.numero })}
                              className="flex size-11 items-center justify-center rounded text-tinta-3 hover:bg-sup-3 sm:size-8"
                              onClick={() => setExcluindo(p)}
                              data-excluir-proposta={p.numero}
                            >
                              <Trash2 className="size-4" aria-hidden />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Estado>
      <Confirmar
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        aoConfirmar={() => excluindo && excluir.mutate(excluindo.id)}
        carregando={excluir.isPending}
        perigoso
        titulo={t('crm.proposta.excluirTitulo', { numero: excluindo?.numero ?? '' })}
        texto={t('crm.proposta.excluirTexto')}
      />
      {ver && (
        <Modal
          aberto
          largura="lg"
          aoFechar={() => setVer(null)}
          titulo={t('crm.proposta.numero', { numero: ver.numero })}
          rodape={
            <>
              {/* Val5 (29/10): enviar a proposta por e-mail pede `pipeline.enviar`. */}
              {podeEditar && s.pode('pipeline.enviar') && (
                <Botao
                  icone={<Mail className="size-4" />}
                  onClick={() => {
                    setEmail(ver);
                    setVer(null);
                  }}
                  data-enviar-proposta=""
                >
                  {t('crm.proposta.enviarEmail')}
                </Botao>
              )}
              <Botao variante="primario" icone={<Printer className="size-4" />} onClick={() => abrirPdfDaProposta(ver.id)} data-imprimir-proposta="">
                {t('crm.proposta.imprimir')}
              </Botao>
            </>
          }
        >
          <DocumentoProposta p={ver} cliente={op.parceiro_nome} contato={contato} />
        </Modal>
      )}
      {semProdutos && (
        <Modal
          aberto
          aoFechar={() => setSemProdutos(false)}
          titulo={t('crm.ficha2.semProdutosTitulo')}
          rodape={
            <>
              <Botao variante="fantasma" onClick={() => setSemProdutos(false)}>
                {t('crm.ficha2.agoraNao')}
              </Botao>
              <Botao
                variante="primario"
                data-ir-produtos=""
                onClick={() => {
                  setSemProdutos(false);
                  navegar(`${caminhoDaNegociacao(op.id)}?aba=produtos`);
                }}
              >
                {t('crm.ficha2.irProdutos')}
              </Botao>
            </>
          }
        >
          <p className="text-sm text-tinta-2" data-aviso-sem-produtos="">
            {t('crm.ficha2.semProdutosTexto')}
          </p>
        </Modal>
      )}
      {email && outros.isFetched && (
        <EmailNegociacao
          op={op}
          proposta={email}
          semOutros={!outros.data}
          inicial={{ assunto: t('crm.email.assuntoProposta', { numero: email.numero, titulo: op.titulo }), texto: textoDoEmail(t, contato, s.usuario.nome, outros.data?.texto) }}
          aoFechar={() => setEmail(null)}
        />
      )}
    </section>
  );
}

/** Página "Nova proposta" (`/crm/comercial/negociacao/:id/proposta`). */
export function NovaProposta() {
  const { t } = useTranslation('pipeline');
  const { id = '' } = useParams();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  // Lista de 30/10 B (item 6): o Voltar volta à tela de onde a pessoa veio; sem ela no app, à negociação.
  const voltar = useVoltar(caminhoDaNegociacao(id)).voltar;
  const op = useQuery({ queryKey: ['pipeline', 'oportunidade', id], queryFn: () => get<Oportunidade>(`/pipeline/oportunidades/${id}`) });
  const ficha = useQuery({ queryKey: ['pipeline', 'crm', 'ficha', id], queryFn: () => get<{ contatos: { nome: string; principal: boolean }[] }>(`/pipeline/crm/negociacoes/${id}/ficha`) });
  const outros = useOutrosServicos();
  const modelos = useModelosProposta();
  const publicados = (modelos.data?.itens ?? []).filter((m) => m.publicado);
  const [modeloId, setModeloId] = useState('');
  const modelo = publicados.find((m) => m.id === modeloId) ?? publicados.find((m) => m.padrao) ?? publicados[0];
  const [textos, setTextos] = useState<{ titulo: string; apresentacao: string; condicoes: string; rodape: string } | null>(null);
  // Lote crm4 (L9): os três blocos entram no editor com a formatação do modelo (o de antes do editor, em parágrafos).
  useEffect(() => {
    if (modelo)
      setTextos({
        titulo: modelo.titulo,
        apresentacao: htmlDoCampo(modelo.apresentacao_html, modelo.apresentacao),
        condicoes: htmlDoCampo(modelo.condicoes_html, modelo.condicoes),
        rodape: htmlDoCampo(modelo.rodape_html, modelo.rodape),
      });
  }, [modelo?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- os textos voltam aos do modelo só quando ele muda
  const [depois, setDepois] = useState<{ acao: 'imprimir' | 'email'; proposta: Proposta } | null>(null);
  const [criando, setCriando] = useState(false);
  const contato = ficha.data?.contatos.find((c) => c.principal)?.nome ?? ficha.data?.contatos[0]?.nome ?? null;
  const o = op.data;
  if (!o || !modelo || !textos) return <Estado consulta={op}>{() => null}</Estado>;
  const itens: ItemProposta[] = (o.itens ?? []).map((i) => ({
    descricao: i.descricao,
    servico_nome: i.servico_nome,
    quantidade: i.quantidade,
    preco_centavos: i.preco_centavos,
    desconto_centavos: i.desconto_centavos ?? 0,
    desconto_tipo: i.desconto_tipo ?? 'valor',
    desconto_percentual: i.desconto_percentual ?? null,
    recorrencia: i.recorrencia,
    total_centavos: Math.round(i.quantidade * i.preco_centavos) - (i.desconto_centavos ?? 0),
  }));
  const previa = { cor: modelo.cor, titulo: textos.titulo, ...blocosDaPrevia(textos), itens, total_centavos: itens.reduce((x, i) => x + i.total_centavos, 0), moeda: o.moeda };
  const criar = async (acao: 'imprimir' | 'email' | null) => {
    // Item 18: a aba do PDF abre já no clique (depois da espera, o navegador bloquearia a janela nova); recebe o endereço
    // quando a proposta existe, ou fecha se a criação falhar.
    const aba = acao === 'imprimir' ? window.open('', '_blank') : null;
    if (aba) aba.opener = null;
    setCriando(true);
    try {
      const novo = crypto.randomUUID();
      await post('/pipeline/propostas', { id: novo, oportunidade_id: o.id, modelo_id: modelo.id, titulo: textos.titulo, apresentacao_html: textos.apresentacao, condicoes_html: textos.condicoes, rodape_html: textos.rodape });
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      toast.sucesso(t('crm.proposta.criada'));
      const lista = await get<{ itens: Proposta[] }>(`/pipeline/oportunidades/${o.id}/propostas`);
      const criada = lista.itens.find((x) => x.id === novo);
      if (acao === 'imprimir' && criada && aba) aba.location.href = caminhoDoPdfDaProposta(criada.id);
      else aba?.close();
      if (acao && criada) setDepois({ acao, proposta: criada });
      else navegar(`${caminhoDaNegociacao(o.id)}?aba=propostas`);
    } catch (e) {
      aba?.close();
      toast.erro(mensagemDeErro(e));
    } finally {
      setCriando(false);
    }
  };
  const mudar = (k: keyof NonNullable<typeof textos>, v: string) => setTextos((x) => (x ? { ...x, [k]: v } : x));
  return (
    <div className="flex min-w-0 flex-col gap-5" data-nova-proposta="">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 items-start gap-2">
          <Botao variante="fantasma" aria-label={t('crm.ficha.voltar')} icone={<ArrowLeft className="size-4" />} onClick={voltar} />
          <div className="min-w-0">
            <p className="kicker">{textoLivre(o.titulo, o.parceiro_nome)}</p>
            <h1 className="mt-1 flex flex-wrap items-center gap-2 text-2xl font-bold tracking-tight">
              {t('crm.proposta.nova')}
              {/* Ajustes do CRM (27/10, F8): a tag do modelo escolhido ("Modelo padrão"), como no RD. */}
              <span className="text-base font-normal" data-tag-modelo="">
                <Selo cor={modelo.padrao ? 'azul' : 'neutro'}>{modelo.padrao ? t('crm.ficha2.modeloPadrao') : textoLivre(modelo.nome)}</Selo>
              </span>
            </h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Botao icone={<Printer className="size-4" />} carregando={criando} onClick={() => void criar('imprimir')} data-criar-imprimir="">
            {t('crm.proposta.criarImprimir')}
          </Botao>
          <Botao icone={<Mail className="size-4" />} carregando={criando} onClick={() => void criar('email')} data-criar-email="">
            {t('crm.proposta.criarEmail')}
          </Botao>
          <Botao variante="primario" carregando={criando} onClick={() => void criar(null)} data-criar-proposta-final="">
            {t('crm.proposta.criar')}
          </Botao>
        </div>
      </header>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Rotulo id="nova-proposta-modelo" rotulo={t('crm.proposta.modelo')}>
            <Selecao id="nova-proposta-modelo" data-modelo-proposta="" value={modelo.id} opcoes={publicados.map((m) => ({ valor: m.id, rotulo: m.padrao ? t('crm.proposta.modeloPadrao', { nome: m.nome }) : m.nome }))} onChange={(e) => setModeloId(e.target.value)} />
          </Rotulo>
          <Rotulo id="nova-proposta-titulo" rotulo={t('crm.proposta.campos.titulo')}>
            <EntradaSensivel mascara={null} livre nomesNoTexto={[o.parceiro_nome]} id="nova-proposta-titulo" maxLength={200} value={textos.titulo} onChange={(e) => mudar('titulo', e.target.value)} />
          </Rotulo>
          {(['apresentacao', 'condicoes', 'rodape'] as const).map((k) => (
            <Rotulo key={k} id={`nova-proposta-${k}`} rotulo={t(`crm.proposta.campos.${k}`)}>
              <EditorRico id={`nova-proposta-${k}`} rotulo={t(`crm.proposta.campos.${k}`)} linhas={k === 'condicoes' ? 6 : 3} valor={textos[k]} aoMudar={(v) => mudar(k, v)} dado={`proposta-${k}`} />
            </Rotulo>
          ))}
        </div>
        <div className="min-w-0">
          <p className="mb-2 text-xs font-semibold text-tinta-3 uppercase">{t('crm.proposta.previa')}</p>
          <DocumentoProposta p={previa} cliente={o.parceiro_nome} contato={contato} />
        </div>
      </div>
      {depois?.acao === 'email' && outros.isFetched && (
        <EmailNegociacao
          op={o}
          proposta={depois.proposta}
          semOutros={!outros.data}
          inicial={{ assunto: t('crm.email.assuntoProposta', { numero: depois.proposta.numero, titulo: o.titulo }), texto: textoDoEmail(t, contato, s.usuario.nome, outros.data?.texto) }}
          aoFechar={() => {
            setDepois(null);
            navegar(`${caminhoDaNegociacao(o.id)}?aba=propostas`);
          }}
        />
      )}
      {depois?.acao === 'imprimir' && (
        <Modal
          aberto
          aoFechar={() => navegar(`${caminhoDaNegociacao(o.id)}?aba=propostas`)}
          titulo={t('crm.proposta.numero', { numero: depois.proposta.numero })}
          rodape={
            <Botao variante="primario" icone={<Printer className="size-4" />} onClick={() => abrirPdfDaProposta(depois.proposta.id)} data-abrir-pdf-proposta="">
              {t('crm.proposta.imprimirDeNovo')}
            </Botao>
          }
        >
          <p className="text-sm" data-proposta-impressa="">
            {t('crm.proposta.impressaAberta')}
          </p>
        </Modal>
      )}
    </div>
  );
}
